import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { CanvasJSAngularChartsModule } from '@canvasjs/angular-charts';
import { IChartWidgetConfig, IChartData } from '../chart/chart.component';
import { BaseWidgetComponent } from '../widget/base/base-widget';
import { WidgetDataService } from '../widget/data/widget-data.service';
import { RemoteDataWrapperService } from 'src/app/shared/services/remote-data/remote-data-wrapper.service';
import { Subscription, takeUntil } from 'rxjs';
import { DashboardDataService } from '../../data/dashboard-data.service';
import { MachineInfoMainService } from '../../../../shared/services/machine-info.service';

/**
 * Chart widget component based on CanvaJS chart component.
 *
 * @export
 * @class ChartCanvaWidgetComponent
 * @extends {BaseWidgetComponent<IChartWidgetConfig>}
 * @implements {OnDestroy}
 * @implements {AfterViewInit}
 * @implements {AfterViewChecked}
 * @implements {OnInit}
 */
@Component({
  selector: 'app-chart-canva',
  standalone: true,
  imports: [CanvasJSAngularChartsModule],
  templateUrl: './chart-canva.component.html',
  styleUrl: './chart-canva.component.scss'
})
export class ChartCanvaWidgetComponent extends BaseWidgetComponent<IChartWidgetConfig> implements OnDestroy, AfterViewInit, AfterViewChecked, OnInit{
  
  /**
   * Data source.
   *
   * @private
   * @type {(IChartData[] | IChartData[][])}
   * @memberof ChartCanvaComponent
   */
  private _dataPoins: IChartData[] | IChartData[][] = [];
  
  /**
   * Canvas chart config.
   *
   * @type {*}
   * @memberof ChartCanvaComponent
   */
  public canvasChart?: any;
  
  /**
   * Subscription of data from client.
   *
   * @private
   * @type {Subscription}
   * @memberof ChartCanvaComponent
   */
  private _dataSubscription?: Subscription;
  
  /**
   * Chart width.
   *
   * @private
   * @type {number}
   * @memberof ChartCanvaComponent
   */
  private _chartWidth: number = 100;
  
  /**
   * Chart height.
   *
   * @private
   * @type {number}
   * @memberof ChartCanvaComponent
   */
  private _chartHeight: number = 100;

  constructor(
    readonly _widgetDataService: WidgetDataService<IChartWidgetConfig>, 
    readonly _remoteDataWrapperService: RemoteDataWrapperService,
    private readonly _cd: ChangeDetectorRef,
    private readonly _dashboardDataService: DashboardDataService,
  ) {
    super(_widgetDataService, _remoteDataWrapperService);
  }

  /**
   * Options setter with logic.
   *
   * @protected
   * @memberof ChartCanvaComponent
   */
  protected override setOptions(options: IChartWidgetConfig | undefined): void {
    if (options) {
      if (this.options && this.canvasChart) {
        // If machine changed, decrement used connection of previous machine
        if (this.options.machineName && this.options.machineName !== options.machineName) {
          this._remoteDataWrapperService.getClient(this.options.machineName).decrementUsedConnections(this.options.series);
          this._dataPoins = [];
          this.canvasChart = {...this.canvasChart};
          this._dataSubscriber(options);
        } else {
          // If machine not changed but series changed, decrement used connection of previous series
          if (options.machineName && this.options.series !== options.series) {
            this._remoteDataWrapperService.getClient(options.machineName).decrementUsedConnections(this.options.series);
            this._dataPoins = [];
            this.canvasChart = {...this.canvasChart};
            this._dataSubscriber(options);
          }
        } 
      }

      this._initChart(options);

      // First sub after widget is required.
      if (!this.options) {
        this._dataSubscriber(options);
      }

      this.options = {...options};
    }
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this._dashboardDataService.widgetInitialized();
  }

  ngAfterViewInit(): void {
    this.widgetDataService.onResize$.pipe(takeUntil(this.unSubscriber)).subscribe(val => {
      if (val && (this._chartWidth !== val.width || this._chartHeight !== val.height)) {
        this._chartWidth = val.width;
        this._chartHeight = val.height;

        if (this.canvasChart) {
          this.canvasChart = { ...this.canvasChart, width: this._chartWidth, height: this._chartHeight };
        }
      }
    });
  }

  ngAfterViewChecked(): void {
    this._cd.detectChanges();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    
    if (this.options?.machineName) {
      this.remoteDataWrapperService.getClient(this.options.machineName).decrementUsedConnections(this.options.series);
    }

    this._cd.detach();

    this._dashboardDataService.widgetDestroyed();
  }

  
  /**
   * Chart initialization logic.
   *
   * @private
   * @param {IChartWidgetConfig} options
   * @memberof ChartCanvaWidgetComponent
   */
  private _initChart(options: IChartWidgetConfig): void {
    this.canvasChart = {
      animationEnabled: true,
      axisX: {
        labelFontColor: '#FFFFFF'
      },
      axisY: {
        scaleBreaks: {
          autoCalculate: true
        },
        labelFontColor: '#FFFFFF'
      },
      data: [{
        type: options.graphType === 'lineSeries' ? 'line' : 'column',
        dataPoints: this._dataPoins,
        color: '#F08080'
      }],
      width: this._chartWidth,
      height: this._chartHeight,
      backgroundColor: '#363640'
    };

    if (options.graphType === 'lineSeries') {
      this.canvasChart.axisX.valueFormatString = "HH:mm";
    }

    if (options.valueAxisAutoSacle) {
      this.canvasChart.axisY.minimum = null;
      this.canvasChart.axisY.maximum = null;
      this.canvasChart.axisY.interval = options.tickInterval;
    } else {
      this.canvasChart.axisY.interval = null;
      this.canvasChart.axisY.minimum = options.valueVisualRange?.min ?? 0;
      this.canvasChart.axisY.maximum = options.valueVisualRange?.max ?? 100;
    }

    this.canvasChart = {...this.canvasChart};
  }

  /**
   *
   *
   * @private
   * @param {IChartWidgetConfig} options
   * @memberof ChartCanvaWidgetComponent
   */
  private _dataSubscriber(options: IChartWidgetConfig): void {
    if (!options?.machineName || !options?.series) {
      return;
    }

    this._dataSubscription?.unsubscribe();

    this._dataSubscription = this.remoteDataWrapperService.getData(options.machineName, options.series).pipe(takeUntil(this.unSubscriber)).subscribe((val) => {

      // line series chart
      // ValueTuple<long, ValT>[]    (DateTime, Value)[]
      // ValueTuple<long, ValT>[][]  (DateTime, Value)[][]

      // where ValueTuple is any[]

      if (this.options?.graphType === 'lineSeries') {

        /********************/
        /* Multi line chart */
        /********************/

        this.appendData(val.Data.map(d => {
          const typed: Array<number> = d as Array<number>;
          return {x: new Date(typed[0]), y: typed[1]} as IChartData
        }));
      } else {

        /*******************/
        /* Multi bar chart */
        /*******************/

        this._dataPoins.length = 0;

        // multi bar chart 
        // ValueTuple<byte, ValT>[]     (index, Value)[]
        // ValueTuple<byte, ValT>[][]   (index, Value)[][]

        // ValueTuple<byte, ValT, string, string>[]     (index, Value, Color, Hint)[]
        // ValueTuple<byte, ValT, string, string>[][]   (index, Value, Color, Hint)[][]

        // where ValueTuple is any[]

        // examine case : al.Data is (index, Value, Color, Hint)[][] or (index, Value)[][]
        if (
          Array.isArray(val.Data) && val.Data.length > 0 && 
          Array.isArray(val.Data[0]) && val.Data[0].length > 0  && 
          Array.isArray(val.Data[0][0])
        ) {
            if(val.Data[0][0].length == 4) {
              // (index, Value, Color, Hint)[][]

              type ValueTuple4 = [number, number, string, string];

              const incomingData: Array<Array<ValueTuple4>> = val.Data as any;
              //console.log(incomingData);
              (this._dataPoins as IChartData[][]).push(...incomingData.map(arr => arr.map(d => ({label: `${d[0]}`, y: d[1], color: d[2], title: d[3], borderColor: '#007bff', borderThickness: 4}))));

            } else if(val.Data[0][0].length == 2){
              // (index, Value)[][]

              type ValueTuple2 = [number, number];

              const incomingData: Array<Array<ValueTuple2>> = val.Data as any;
              //console.log(incomingData);
              (this._dataPoins as IChartData[][]).push(...incomingData.map(arr => arr.map(d => ({label: `${d[0]}`, y: d[1], color: '#cccccc', title: `${d[0]}`, borderColor: '#007bff', borderThickness: 4}))));
            }

            if(this._dataPoins.length > 0) {
              this.canvasChart.axisY.labelFontColor = '#FFFFFF';
              this.canvasChart.toolTip = { content: "{title}: {y}" };
    
              this.canvasChart.data = this._dataPoins.map(arr => {
                return {
                  type: 'column',
                  dataPoints: arr
                }
              });
            }

        } else if(
          Array.isArray(val.Data) && val.Data.length > 0 && 
          Array.isArray(val.Data[0])
        ) {
          if(val.Data[0].length == 4) {
            // (index, Value, Color, Hint)[]

            type ValueTuple4 = [number, number, string, string];

            const incomingData: Array<ValueTuple4> = val.Data as any;
            //console.log(incomingData);
            (this._dataPoins as IChartData[]).push(...incomingData.map(d => ({label: `${d[0]}`, y: d[1], color: d[2], title: d[3], borderColor: '#007bff', borderThickness: 4})));

          } else if(val.Data[0].length == 2){
            // (index, Value)[]

            type ValueTuple2 = [number, number];

            const incomingData: Array<ValueTuple2> = val.Data as any;
            //console.log(incomingData);
            (this._dataPoins as IChartData[]).push(...incomingData.map(d => ({label: `${d[0]}`, y: d[1], color: '#cccccc', title: `${d[0]}`, borderColor: '#007bff', borderThickness: 4})));
          }
        }

        

        // if (Array.isArray(val.Data) && val.Data.length > 0 && Array.isArray(val.Data[0]) && val.Data.length && Array.isArray(val.Data[0])) {
          
        //   const incomingData: Array<Array<{Key: string; Value: number; Color: string; Hint: number;}>> = val.Data as any;
        //   console.log(incomingData);
        //   (this._dataPoins as IChartData[][]).push(...incomingData
        //     .map(arr => arr.map(d => ({label: `${d.Hint}`, y: d.Value, color: d.Color, title: d.Key})))
        //   );
          
        //   this.canvasChart.axisY.labelFontColor = '#FFFFFF';
        //   this.canvasChart.toolTip = { content: "{title}: {y}" };

        //   this.canvasChart.data = incomingData.map(arr => {
        //     return {
        //       type: 'column',
        //       dataPoints: arr.map(d => ({label: `${d.Hint}`, y: d.Value, color: d.Color, title: d.Key, borderColor: '#007bff', borderThickness: 4})),
        //     }
        //   });

        // } else {
        //   // single bar chart
        //   (this._dataPoins as IChartData[]).push(...(val.Data as Array<{Key: number; Value: number;}>).map(d => ({ x: d.Key, y: d.Value })));
        // }
      }

      this.canvasChart = {...this.canvasChart};
    });
  }

  
  /**
   * Handles buffer size of data source for line serie data.
   *
   * @param {IChartData[]} data
   * @memberof ChartCanvaWidgetComponent
   */
  public appendData(data: IChartData[]): void {
    const tmp: IChartData[] = [...this._dataPoins as IChartData[], ...data];

    this._dataPoins.length = 0;
    if (tmp.length > (this.options?.bufferSize ?? 100)) {
      (this._dataPoins as IChartData[]).push(...tmp.slice(tmp.length-(this.options?.bufferSize ?? 100)));
    } else {
      (this._dataPoins as IChartData[]).push(...tmp);
    }
  }
}
